import { FormCommonProps } from "src/components/common/forms/types.ts";
import { DeepPartial, useForm } from "react-hook-form";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { AavoForm } from "src/components/common/forms/AavoForm.tsx";
import i18n from "i18next";
import { requiredNonNegativeIntegerRule } from "src/components/common/forms/validation.ts";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { NewDocumentFileCreationType } from "src/api/generated/documents/newDocumentFileCreationType.ts";
import { DocumentRevision } from "src/api/generated/postgres/db/types/documents/tables/documentRevision.ts";
import { DocumentRevisionApi } from "src/api/generated/documents/api/documentRevisionApi.ts";
import { uploadFile } from "src/utils/fileUploading";
import { DocumentQueryApi } from "src/api/generated/documents/api/documentQueryApi";
import { openWopiFilePage } from "./wopi/wopiFilePageUtils";
import { DocumentFileCreationFormPart } from "src/components/views/documents/DocumentFileCreationFormPart.tsx";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox";

export interface DocumentRevisionFormProps extends FormCommonProps<number> {
	documentId: number;
	documentRevision: DocumentRevision | undefined;
}

export const DocumentRevisionForm = (props: DocumentRevisionFormProps) => {
	const { documentId, documentRevision } = props;

	const fetchDefaultValues = async (): Promise<DeepPartial<DocumentRevisionFormValues>> => {
		if (documentRevision !== undefined)
			return documentRevision;
		else {
			const defaultRevisionNumber = await DocumentRevisionApi.getNextDocumentRevisionNumber({
				documentId
			});
			return {
				revisionNumber: defaultRevisionNumber,
				release: true
			};
		}
	};

	return (
		<AsyncFetchRender
			fetch={fetchDefaultValues}
			content={(defaultValues) =>
				<DocumentRevisionFormContent
					defaultValues={defaultValues}
					{...props} />}
		/>
	);
};

interface DocumentRevisionFormValues {
	revisionNumber: number;
	note: string;
	release: boolean;
	file: File;
	fileCreationType: NewDocumentFileCreationType;
}

interface DocumentRevisionFormContentProps extends DocumentRevisionFormProps {
	defaultValues: DeepPartial<DocumentRevisionFormValues>;
}

const DocumentRevisionFormContent = (
	{
		documentId,
		documentRevision,
		defaultValues,
		...other
	}: DocumentRevisionFormContentProps) => {

	const {
		control,
		handleSubmit,
		formState: { isDirty },
		watch
	} = useForm<DocumentRevisionFormValues>({
		mode: "all",
		defaultValues: {
			...defaultValues,
			fileCreationType: "UPLOAD"
		}
	});

	const minRevision = defaultValues.revisionNumber ?? 1;

	return (
		<AavoForm
			handleSubmit={handleSubmit}
			isDirty={isDirty}
			submit={(formValues) => submitForm(documentRevision?.documentRevisionId, documentId, formValues)}
			columns={1}
			{...other}
		>
			<FormTextField
				control={control}
				name={"revisionNumber"}
				rules={requiredNonNegativeIntegerRule(
					(value: number) => {
						if (value < minRevision) {
							return i18n.t("revision_must_be_at_least", { minRevision });
						}
						return true;
					}
				)}
				label={i18n.t("revision")}
			/>
			{
				documentRevision === undefined &&
				<FormCheckbox
					control={control}
					name={"release"}
					label={i18n.t("release")}
				/>
			}
			<FormTextField
				control={control}
				name={"note"}
				label={i18n.t("note")}
				multiline={true}
				minRows={5}
			/>
			<DocumentFileCreationFormPart
				control={control}
				watch={watch}
				isExistingRecord={documentRevision !== undefined}
			/>
		</AavoForm>
	);
};

const submitForm = async (
	inputDocumentRevisionId: number | undefined,
	documentId: number,
	formValues: DocumentRevisionFormValues
): Promise<number> => {
	const fileHandle = formValues.file
		? await uploadFile(formValues.file)
		: undefined;

	let documentRevisionId;
	if (inputDocumentRevisionId !== undefined) {
		await DocumentRevisionApi.updateDocumentRevision({
			documentRevisionId: inputDocumentRevisionId,
			revisionNumber: formValues.revisionNumber,
			note: formValues.note,
			newFile: fileHandle
		});
		documentRevisionId = inputDocumentRevisionId;
	} else {
		documentRevisionId = await DocumentRevisionApi.createDocumentRevision({
			documentId: documentId,
			revisionNumber: formValues.revisionNumber,
			release: formValues.release,
			note: formValues.note,
			fileCreationType: formValues.fileCreationType,
			file: fileHandle
		});
		if (formValues.fileCreationType !== "UPLOAD") {
			const fileUuid = await DocumentQueryApi.getDocumentRevisionPrimaryFileUuid({
				documentRevisionId
			});
			openWopiFilePage("edit_new", fileUuid);
		}
	}

	return documentRevisionId;
};
